import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchRemovalOrders,
  imogenSelector,
} from "../../../../../redux/slices/imogen";

const STALENESS_HOURS = 48;

const formatDate = (iso) => {
  if (!iso) return "—";
  return moment.utc(iso).format("MMM D, YYYY");
};

const formatSourceTimestamp = (iso) => {
  if (!iso) return "unknown";
  return moment.utc(iso).format("MMM D, YYYY");
};

const isStale = (iso) => {
  if (!iso) return true;
  return moment.utc().diff(moment.utc(iso), "hours") > STALENESS_HOURS;
};

const RemovalOrders = ({ store, marketplace }) => {
  const dispatch = useDispatch();
  const {
    removalOrders: data,
    removalOrdersLoading: loading,
    removalOrdersErrorMessage: errorMessage,
  } = useSelector(imogenSelector);

  useEffect(() => {
    dispatch(fetchRemovalOrders({ store, marketplace }));
  }, [dispatch, store, marketplace]);

  const sourceFreshness = useMemo(() => {
    if (!data?.data_freshness) return null;
    const ts = data.data_freshness.fba_removal_orders_latest;
    return { timestamp: ts, stale: isStale(ts) };
  }, [data]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Alert severity="error">
        Failed to load removal orders: {errorMessage}
      </Alert>
    );
  }

  if (!data) return null;

  const items = data.items ?? [];
  const lastRefresh = data.computed_at
    ? moment.utc(data.computed_at).format("MMM D, HH:mm [UTC]")
    : null;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        flexWrap="wrap"
        mb={1}
      >
        <Box>
          <Typography variant="h6">
            Removal orders{items.length > 0 ? ` (${items.length})` : ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Amazon-initiated removals of 15+ units in the last 7 days.
            Seller-initiated removals are excluded.
          </Typography>
        </Box>
        {lastRefresh && (
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastRefresh}
          </Typography>
        )}
      </Box>

      {sourceFreshness && (
        <Typography
          variant="caption"
          color={sourceFreshness.stale ? "warning.main" : "text.secondary"}
          sx={{ display: "block", mb: 2 }}
        >
          FBA removal orders last updated{" "}
          {formatSourceTimestamp(sourceFreshness.timestamp)}
          {sourceFreshness.stale && " — data may be stale"}
        </Typography>
      )}

      {items.length === 0 ? (
        <Box p={3}>
          <Typography variant="body2" color="text.secondary">
            No Amazon-initiated removal orders in the last 7 days.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account · MP</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>FNSKU</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Units</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.item_key}>
                  <TableCell>
                    {item.store} · {item.marketplace}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={item.order_source} arrow>
                      <span style={{ cursor: "help" }}>{item.order_id}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{item.sku}</TableCell>
                  <TableCell>{item.fnsku}</TableCell>
                  <TableCell>{item.order_type}</TableCell>
                  <TableCell>{item.order_status}</TableCell>
                  <TableCell align="right">{item.requested_quantity}</TableCell>
                  <TableCell>{formatDate(item.request_date)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RemovalOrders;
